import { useEffect } from 'react';
import { ClusterApiProvider } from '../api/ClusterApiProvider';
import { ClusterSessionProvider, useClusterSession } from '../auth/ClusterSessionProvider';
import { useI18n } from '../i18n/useI18n';
import { useRouter } from '../router/Router';
import { asClusterRoute } from '../router/routes';
import { CatalogScreen } from '../screens/cluster/CatalogScreen';
import { NodesScreen } from '../screens/cluster/NodesScreen';
import { AuditScreen } from '../screens/cluster/AuditScreen';
import { ClusterLoginScreen } from '../screens/cluster/ClusterLoginScreen';
import { ClusterNoAccessScreen } from '../screens/cluster/ClusterNoAccessScreen';
import { Card, EmptyState, Loading } from '../ui/primitives';
import { IconAudit, IconCatalog, IconNodes } from '../ui/icons';
import { AppShell } from './AppShell';
import type { ShellNav } from './AppShell';

/**
 * The cluster console, on a tenancy's own domain.
 *
 * Everything here belongs to one tenancy, and which one was decided by the host
 * before this component existed — `tenancyDomain` is here to be shown, not to
 * be sent anywhere. The way in is the identity provider that tenancy chose in
 * the account: a provider account opens nothing on this domain, and cannot,
 * because the only thing that produces a caller here is a row an account
 * session is never written to.
 *
 * A session opens exactly one screen, and the role it opens it for is checked
 * by the server on every call it makes. Somebody the provider vouched for whom
 * this tenancy's policy does not name an administrator gets no session at all,
 * so `role: "none"` stays representable and does not occur.
 */
export function ClusterApp({ tenancyDomain }: { tenancyDomain: string }) {
  return (
    <ClusterApiProvider>
      <ClusterSessionProvider>
        <ClusterRoutes tenancyDomain={tenancyDomain} />
      </ClusterSessionProvider>
    </ClusterApiProvider>
  );
}

function ClusterRoutes({ tenancyDomain }: { tenancyDomain: string }) {
  const { t } = useI18n();
  const { status, session, signOut } = useClusterSession();
  const { route, navigate } = useRouter();

  const clusterRoute = asClusterRoute(route);

  // A session that has just been opened leaves the browser on `/login` — the
  // address the provider returned to. Once there is a session that address is
  // the one page here nobody needs, so the console steps off it, replacing the
  // entry rather than adding one: Back should not lead to a sign-in already
  // made.
  useEffect(() => {
    if (session !== null && route.name === 'cluster.login') {
      navigate({ name: 'cluster.catalog' }, { replace: true });
    }
  }, [session, route, navigate]);

  if (status === 'loading') return <Loading />;
  if (session === null) return <ClusterLoginScreen tenancyDomain={tenancyDomain} />;
  if (session.role !== 'cluster_admin') return <ClusterNoAccessScreen />;

  const nav: ShellNav[] = [
    {
      group: t('nav.cluster.group'),
      items: [
        {
          route: { name: 'cluster.catalog' },
          label: t('nav.cluster.catalog'),
          icon: <IconCatalog />,
          matches: ['cluster.catalog'],
        },
        {
          route: { name: 'cluster.nodes' },
          label: t('nav.cluster.nodes'),
          icon: <IconNodes />,
          matches: ['cluster.nodes'],
        },
        {
          route: { name: 'cluster.audit' },
          label: t('nav.cluster.audit'),
          icon: <IconAudit />,
          matches: ['cluster.audit'],
        },
      ],
    },
  ];

  const shell = {
    scope: { name: session.tenancy.display_name, sub: session.tenancy.tenancy_domain },
    actor: { name: session.actor.display_name, role: t('shell.role.cluster') },
    nav,
    onSignOut: () => {
      void signOut();
    },
  };

  if (clusterRoute === null) {
    return (
      <AppShell {...shell} crumb={t('route.notFound.title')}>
        <div className="content-inner">
          <Card>
            <EmptyState title={t('route.notFound.title')} body={t('route.notFound.body')} />
          </Card>
        </div>
      </AppShell>
    );
  }

  if (clusterRoute.name === 'cluster.nodes') {
    return (
      <AppShell {...shell} crumb={t('nodes.title')}>
        <NodesScreen />
      </AppShell>
    );
  }

  if (clusterRoute.name === 'cluster.audit') {
    return (
      <AppShell {...shell} crumb={t('audit.title')}>
        <AuditScreen />
      </AppShell>
    );
  }

  return (
    <AppShell {...shell} crumb={t('catalog.title')}>
      <CatalogScreen />
    </AppShell>
  );
}
